Ext.define('SA.SimmonsCrosstabModel', {
    alias: 'view.simmonsCrosstab',
    extend: 'SA.data.ReportView',

    mixins: {
        locations: 'SA.data.mixin.OneLocation'
    },

    statics: {
        create({project, country, censusRelease, name, attributes}) {
            var dataset = SA.data.Vendors.getSimmonsCrosstabModel(censusRelease),
                year = dataset.getLatestEditionForCensusReleaseYear(censusRelease),
                attributes = attributes.filter(id => this.supportsAttributeForYear(id, year)),
                verticalAttributes = attributes.filter(id => SA.data.AttributesCache.hasDefaultVerticalPosition(id)),
                horizontalAttributes = attributes.filter(id => !SA.data.AttributesCache.hasDefaultVerticalPosition(id)),
                data = {
                    type: 'simmonsCrosstab',
                    buildingBlocksCensusRelease: censusRelease,
                    filterVisibility: 'strikeout',
                    horizontalAttributes,
                    locationItem: SA.data.LocationsCache.toLocationItem(country.getLocationSeries()),
                    name,
                    verticalAttributes
                };

            return new SA.SimmonsCrosstabModel({project, year, data});
        },

        supportsAttributeForYear(attribute, year) {
            var hasMatchingYear = year === SA.data.AttributesCache.get(attribute).year,
                hasNecessaryType = SA.data.AttributesCache.isSimmonsCrosstabAttribute(attribute);

            return hasMatchingYear && hasNecessaryType;
        }
    },

    locationProperty: 'locationItem',
    maxCellCount: 1600,

    constructor({year, data}) {
        this.callParent(arguments);

        this.year = year || Number(/->(\d{4})/.exec(data.verticalAttributes[0])[1]);

        this.attributes = new SA.CrosstabAttributes({top: data.verticalAttributes, left: data.horizontalAttributes});
    },

    addAttribute(id) {
        this.addAttributes([id]);
    },

    addAttributes(ids) {
        var isDefaultVertical = id => SA.data.AttributesCache.hasDefaultVerticalPosition(id),
            appendAttribute = id => this.attributes.append({attribute: id, top: isDefaultVertical(id)});

        ids.filter(this.supportsAttribute, this).forEach(appendAttribute);

        this.project.addAttributes(ids);

        this.onChangeAttribute();
    },

    addLocation(location) {
        this.project.addLocation(location);

        if (!this.supportsLocation(location)) {
            return false;
        }

        return this.setLocation(location);
    },

    addProjectAttributes() {
        this.addAttributes(this.getProject().getAttributes());
    },

    capture() {
        return {
            type: this.getType(),
            buildingBlocksCensusRelease: this.getCensusReleaseYear(),
            name: this.getName(),
            locationItem: SA.data.LocationsCache.toLocationItem(this.getLocation()),
            horizontalAttributes: this.attributes.getSide({left: true}).slice(),
            verticalAttributes: this.attributes.getSide({top: true}).slice()
        };
    },

    clearAttributes() {
        this.attributes.clear();

        this.onChangeAttribute();
    },

    cloneAttributes() {
        return this.getAttributesModel().clone();
    },

    createController() {
        return new SA.controller.SimmonsCrosstabController(this);
    },

    createDataMap() {
        return {};
    },

    getAttributeArrays() {
        return this.attributes.getArrays();
    },

    getAttributes() {
        return this.attributes.getAll();
    },

    getAttributesModel() {
        return this.attributes;
    },

    getCountryLocationSeries() {
        return SA.Country.get(this.getProject().getCountry()).getLocationSeries();
    },

    getDataQuery() {
        return [
            ...SA.data.AttributesQuery.getCountryAndStatusVisiblePredicate(this.getCountry(), this.getCensusRelease()),
            ['=', 'year', this.getYear()],
            ['in', 'dataset_series', [SA.DatasetIds.MRISimmonsCrosstab, SA.DatasetIds.SimmonsCrosstab]]
        ];
    },

    getDatasetName() {
        var anyAttribute = this.getAttributes()[0];

        return SA.data.AttributesCache.getDatasetName(anyAttribute);
    },

    getUpdates(capture) {
        var updates = {};

        if (this.getId()) {
            updates.id = this.getId();
        }

        if (Ext.Object.isEmpty(this.saved)) {
            return Object.assign(updates, capture);
        }

        if (capture.name !== this.saved.name) {
            updates.name = capture.name;
        }

        if (!Ext.Object.equals(capture.locationItem, this.saved.locationItem)) {
            updates.locationItem = capture.locationItem;
        }

        if (!SA.arraysEqual(capture.horizontalAttributes, this.saved.horizontalAttributes)) {
            updates.horizontalAttributes = capture.horizontalAttributes;
        }

        if (!SA.arraysEqual(capture.verticalAttributes, this.saved.verticalAttributes)) {
            updates.verticalAttributes = capture.verticalAttributes;
        }

        return updates;
    },

    getYear() {
        return this.year;
    },

    hasAttribute(attribute) {
        return this.attributes.has(attribute);
    },

    hasLatestYear() {
        var dataset = SA.data.Vendors.getSimmonsCrosstabModel(this.getCensusReleaseYear()),
            latestYear = dataset.getLatestEditionForCensusReleaseYear(this.getCensusReleaseYear());

        return this.getYear() === latestYear;
    },

    hasTooManyAttributes() {
        var {top, left} = this.attributes.getCounts();

        return (top * left) > this.maxCellCount;
    },

    isChanged() {
        var updates = this.getUpdates(this.capture()),
            updatedProperties = Object.keys(updates).filter(property => property !== 'id');

        return updatedProperties.length > 0;
    },

    isSavable() {
        return this.isViewable();
    },

    isViewable() {
        return !this.needsAttributes() && !this.hasTooManyAttributes();
    },

    needsAttributes() {
        var {top, left} = this.attributes.getCounts();

        return top === 0 || left === 0;
    },

    onChangeAttribute() {
        this.save();

        this.fireEvent('change:attribute');
    },

    removeAttribute(attribute) {
        this.attributes.remove(attribute);
    },

    setAttributesModel(attributes) {
        this.attributes = attributes;

        this.onChangeAttribute();
    },

    supportsAttribute(attribute) {
        return SA.SimmonsCrosstabModel.supportsAttributeForYear(attribute, this.getYear());
    },

    supportsDatasetSeries(series) {
        return SA.data.Vendors.getDatasetModel(series).isSimmonsCrosstab();
    },

    supportsLocation(location) {
        location = SA.data.LocationsCache.get(location);

        return location.locationSeries === this.getCountryLocationSeries();
    },

    toggleAttribute({attribute, side}) {
        this.attributes.toggle({attribute, side});

        this.onChangeAttribute();
    }
});